import { useState, useMemo, useEffect, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getGraphDetails, submitUnit } from "api/weeklyTracker";
import { useTranslation } from "react-i18next";

const WeekGraph = ({
  graph_data,
  detailed_metrics,
  total_weeks_tracked,
  selectedUnit,
  setSelectedUnit,
}) => {
  // const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("weight");
  const { t, i18n } = useTranslation();

  const [selectedWeekRange, setSelectedWeekRange] = useState("");

  // New state for dynamically fetched graph data
  const [currentGraphData, setCurrentGraphData] = useState(graph_data || null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedData, setHasFetchedData] = useState(false); // Track if we've made API calls

  // Memoize the initial data setup
  useEffect(() => {
    // Only set initial data if we haven't fetched data yet for a range
    if (!currentGraphData && graph_data) {
      setCurrentGraphData(graph_data);
    }
  }, [graph_data, currentGraphData]);

  // Generate week ranges based on total_weeks_tracked
  // const weekRanges = useMemo(() => {
  //   if (!total_weeks_tracked || total_weeks_tracked === 0) return [];

  //   const ranges = [];
  //   const totalWeeks = parseInt(total_weeks_tracked);

  //   // Calculate how many full 5-week ranges we have
  //   const fullRanges = Math.floor(totalWeeks / 5);
  //   const remainingWeeks = totalWeeks % 5;

  //   // Create full 5-week ranges
  //   for (let i = 0; i < fullRanges; i++) {
  //     const start = i * 5 + 1;
  //     const end = (i + 1) * 5;
  //     ranges.push({
  //       label: `Week ${start} - ${end}`,
  //       value: `${start}-${end}`,
  //       start,
  //       end,
  //     });
  //   }

  //   // Add remaining weeks if any
  //   if (remainingWeeks > 0) {
  //     const start = fullRanges * 5 + 1;
  //     const end = totalWeeks;
  //     if (start === end) {
  //       ranges.push({
  //         label: `Week ${start}`,
  //         value: `${start}-${end}`,
  //         start,
  //         end,
  //       });
  //     } else {
  //       ranges.push({
  //         label: `Week ${start} - ${end}`,
  //         value: `${start}-${end}`,
  //         start,
  //         end,
  //       });
  //     }
  //   }

  //   return ranges;
  // }, [total_weeks_tracked]);
  const weekRanges = useMemo(() => {
    if (total_weeks_tracked === null || total_weeks_tracked === undefined)
      return [];

    const lastWeek = Number(total_weeks_tracked); // e.g. 15
    const totalWeeks = lastWeek + 1; // weeks start from 0
    const ranges = [];

    for (let start = 0; start < totalWeeks; start += 6) {
      const end = Math.min(start + 5, lastWeek);

      ranges.push({
        label: start === end ? `Week ${start}` : `Week ${start}-${end}`,
        value: `${start}-${end}`,
        start,
        end,
      });
    }

    return ranges;
  }, [total_weeks_tracked]);

  // Set default to latest week range on component mount - ONLY ONCE
  useEffect(() => {
    if (weekRanges.length > 0 && !selectedWeekRange) {
      const latestRange = weekRanges[weekRanges.length - 1].value;
      setSelectedWeekRange(latestRange);
    }
  }, [weekRanges]); // Remove selectedWeekRange from dependencies

  // API call function
  const fetchGraphData = useCallback(async (weekRange) => {
    if (!weekRange) return;

    setIsLoading(true);
    try {
      const response = await getGraphDetails(weekRange);
      setCurrentGraphData(response.graph_data);
      setHasFetchedData(true); // Mark that we've made API calls
    } catch (error) {
      console.error("Error fetching graph details:", error);
      setCurrentGraphData(null); // Clear data on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateUnit = async (unit) => {
    setIsLoading(true);
    try {
      setSelectedUnit(unit);
      await submitUnit(unit);
    } catch (error) {
      console.error("Error in submitting unit:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to fetch data ONLY when selectedWeekRange changes AND it's not the initial load
  useEffect(() => {
    if (!selectedWeekRange) return;

    const rangeObject = weekRanges.find((r) => r.value === selectedWeekRange);
    if (rangeObject && hasFetchedData) {
      // Only fetch if we've already made at least one user-initiated change
      fetchGraphData(rangeObject.value);
    }
  }, [selectedWeekRange, fetchGraphData, weekRanges, hasFetchedData]);

  // Handle week range change
  const handleWeekRangeChange = (e) => {
    const newRange = e.target.value;
    setSelectedWeekRange(newRange);
    // Set flag to indicate we should fetch data for future changes
    if (!hasFetchedData) {
      setHasFetchedData(true);
    }
  };

  // Process graph data with fallback
  const processedData = useMemo(() => {
    if (!currentGraphData) {
      return {
        weightProgress: [],
        waistProgress: [],
        bodyFatProgress: [],
        wellnessProgress: [],
      };
    }

    return {
      weightProgress: currentGraphData.weight_progress || [],
      waistProgress: currentGraphData.waist_progress || [],
      bodyFatProgress: currentGraphData.body_fat_progress || [],
      wellnessProgress: currentGraphData.wellness_progress || [],
    };
  }, [currentGraphData]);

  // Convert weight from kg to lbs
  const kgToLbs = (kg) => {
    if (!kg) return 0;
    return (kg * 2.20462).toFixed(1);
  };

  // Convert chart data based on selected unit
  const convertChartData = (data) => {
    if (!data || selectedUnit !== "lbs") return data;

    return data.map((item) => {
      if (activeTab === "weight" && item.current_weight) {
        return {
          ...item,
          current_weight: parseFloat(kgToLbs(item.current_weight)),
        };
      }
      return item;
    });
  };

  // Get current chart data based on active tab
  const getCurrentChartData = () => {
    let data;
    switch (activeTab) {
      case "weight":
        data = processedData.weightProgress;
        break;
      case "bodyFat":
        data = processedData.bodyFatProgress;
        break;
      case "waist":
        data = processedData.waistProgress;
        break;
      default:
        data = [];
    }

    return convertChartData(data);
  };

  // Get data key based on active tab
  const getDataKey = () => {
    switch (activeTab) {
      case "weight":
        return "current_weight";
      case "bodyFat":
        return "current_body_fat_percentage";
      case "waist":
        return "current_waist_circumference";
      default:
        return "";
    }
  };

  const replaceDecimal = (value) => {
    if (value === null || value === undefined) return "0";

    const stringValue = value.toString();

    if (i18n?.language === "nl") {
      return stringValue.replace(/\./g, ",");
    }
    return stringValue;
  };

  // Format value based on tab
  const formatValue = (value) => {
    if (value === null || value === undefined) return "0";

    switch (activeTab) {
      case "weight":
        return replaceDecimal(`${parseFloat(value)} ${selectedUnit}`);
      case "bodyFat":
        return replaceDecimal(`${parseFloat(value)}%`);
      case "waist":
        return replaceDecimal(`${parseFloat(value)} cm`);
      default:
        return replaceDecimal(parseFloat(value));
    }
  };

  // Get chart label
  const getChartLabel = () => {
    switch (activeTab) {
      case "weight":
        return `${t("Weight")} (${selectedUnit})`;
      case "bodyFat":
        return t("Body Fat %");
      case "waist":
        return t("Waist Circumference");
      default:
        return "";
    }
  };

  const currentChartData = getCurrentChartData();
  const dataKey = getDataKey();
  const hasData = currentChartData && currentChartData.length > 0;

  // Calculate chart width based on number of data points
  const calculateChartWidth = () => {
    const dataPoints = currentChartData.length;
    const minDataPointsForScroll = 6; // Show scroll when more than 6 data points

    if (dataPoints <= minDataPointsForScroll) {
      return "100%";
    }

    // Calculate width based on data points (120px per data point for comfortable spacing)
    const pointsWidth = dataPoints * 120;
    return `${pointsWidth}px`;
  };

  // Check if we need scroll
  const needsScroll = currentChartData?.length > 6;

  const calculateStats = () => {
    const {
      starting_weight = 0,
      goal_weight = 0,
      progress: weightProgress = 0,

      starting_body_fat_percentage = 0,
      goal_body_fat_percentage = 0,
      body_fat_progress = 0,

      starting_waist_circumference = 0,
      goal_waist_circumference = 0,
      waist_circumference_progress = 0,
    } = detailed_metrics || {};

    // Convert weights if needed
    const displayStartingWeight =
      selectedUnit === "lbs" ? kgToLbs(starting_weight) : starting_weight;

    const displayGoalWeight =
      selectedUnit === "lbs" ? kgToLbs(goal_weight) : goal_weight;

    return {
      // Weight stats
      startingWeight: displayStartingWeight,
      goalWeight: displayGoalWeight,
      weightProgress: weightProgress,

      // Body Fat stats
      startingBodyFat: starting_body_fat_percentage,
      goalBodyFat: goal_body_fat_percentage,
      bodyFatProgress: body_fat_progress,

      // Waist stats
      startingWaist: starting_waist_circumference,
      goalWaist: goal_waist_circumference,
      waistProgress: waist_circumference_progress,
    };
  };

  const stats = calculateStats();

  const calculateNiceScale = (min, max, maxTicks = 5) => {
    // Handle edge case where min === max
    if (min === max) {
      const padding = Math.max(1, min * 0.1); // 10% padding or at least 1
      return [Math.max(0, min - padding), max + padding];
    }

    const range = max - min;
    const roughStep = range / (maxTicks - 1);

    // Round to a nice step value
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const fraction = roughStep / magnitude;

    let niceStep;
    if (fraction <= 1) niceStep = 1 * magnitude;
    else if (fraction <= 2) niceStep = 2 * magnitude;
    else if (fraction <= 5) niceStep = 5 * magnitude;
    else niceStep = 10 * magnitude;

    // Calculate nice min and max
    const niceMin = Math.floor(min / niceStep) * niceStep;
    const niceMax = Math.ceil(max / niceStep) * niceStep;

    return [niceMin, niceMax];
  };
  const getYAxisDomain = () => {
    if (!currentChartData || currentChartData.length === 0) {
      return [0, 100];
    }

    const values = currentChartData
      .map((item) => {
        const val = item[dataKey];
        if (val === null || val === undefined || val === "") return NaN;
        return Number(val);
      })
      .filter((value) => Number.isFinite(value));

    if (values.length === 0) {
      return [0, 100];
    }

    const min = Math.min(...values);
    const max = Math.max(...values);

    const [niceMin, niceMax] = calculateNiceScale(min, max);

    // ✅ limit to max 2 decimals
    const round = (num) => Number(num.toFixed(2));

    return [round(niceMin), round(niceMax)];
  };

  return (
    <div className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] p-6 rounded-3xl text-white min-h-[500px]">
      {/* Header */}
      <div className="flex flex-col items-start gap-2 sm:flex-row justify-between sm:items-center mb-3">
        <h2 className="text-[16px] font-[500]">
          {t("Your Physical Transformation Overview")}
        </h2>
        <div className="flex items-center gap-3">
          {weekRanges.length > 0 && (
            <>
              <select
                className="bg-white rounded-lg px-3 py-3 text-[12px] text-[#000] outline-none cursor-pointer"
                value={selectedUnit}
                onChange={(e) => updateUnit(e.target.value)}
                disabled={isLoading}
              >
                <option value="lbs">{t("Lbs")}</option>
                <option value="kg">Kg</option>
              </select>

              <select
                className="bg-white rounded-lg px-3 py-3 text-[12px] text-[#000] outline-none cursor-pointer"
                value={selectedWeekRange}
                onChange={handleWeekRangeChange}
                disabled={isLoading}
              >
                {weekRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      <div className="w-full">
        {/* Chart Section */}
        <div className="bg-white rounded-2xl p-2 md:p-6">
          {/* Chart Tabs */}
          <div className="flex flex-col [@media(min-width:1350px)]:flex-row md:items-center gap-2 justify-between mb-2 [@media(min-width:1350px)]:mb-0">
            <div className="flex flex-col md:flex-row bg-white rounded-full p-1 md:border md:border-[#E9E9E9] gap-0 md:gap-1 mb-2 xl:my-3 gap-2  ">
              <button
                onClick={() => setActiveTab("weight")}
                title="Weight Trend"
                className={`${
                  activeTab === "weight"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                } px-4 py-2 rounded-full text-[10px] md:text-[12px] font-medium transition-colors border border-[#E9E9E9] md:border-none`}
              >
                {t("Weight Trend")}
              </button>
              <button
                onClick={() => setActiveTab("bodyFat")}
                title="Body Fat %"
                className={`${
                  activeTab === "bodyFat"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                } px-4 py-2 rounded-full text-[10px] md:text-[12px] font-medium transition-colors border border-[#E9E9E9] md:border-none`}
              >
                {t("Body Fat %")}
              </button>
              <button
                onClick={() => setActiveTab("waist")}
                title="Waist Circumference"
                className={`${
                  activeTab === "waist"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                } px-4 py-2 rounded-full text-[10px] md:text-[12px] font-medium transition-colors border border-[#E9E9E9] md:border-none`}
              >
                {t("Waist Circumference")}
              </button>
            </div>

            {/* Stats - Only show for weight tab */}
            {detailed_metrics && (
              <div className="flex flex-col md:flex-row md:items-center gap-2">
                {/* Starting stat - shows based on active tab */}
                <div className="text-[#E58B00] text-[12px] bg-[#FBEFDB] rounded-full px-2 py-1 flex items-center gap-2 cursor-default">
                  <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                    📊
                  </p>
                  {activeTab === "weight" &&
                    `${t("Starting Weight")}: ${formatValue(
                      stats.startingWeight,
                    )}`}
                  {activeTab === "bodyFat" &&
                    `${t("Starting Body Fat")}: ${formatValue(
                      stats.startingBodyFat,
                    )}`}
                  {activeTab === "waist" &&
                    `${t("Starting Waist")}: ${formatValue(
                      stats.startingWaist,
                    )}`}
                </div>

                {/* Progress stat - shows based on active tab */}
                <div className="text-[#4CA842] text-[12px] bg-[#D7FFD2] rounded-full px-2 py-1 flex items-center gap-2 cursor-default">
                  <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                    📈
                  </p>
                  {activeTab === "weight" &&
                    `${t("Progress")}: ${
                      stats.weightProgress < 0 ? "-" : ""
                    }${replaceDecimal(
                      Math.abs(stats.weightProgress).toFixed(1),
                    )} %`}

                  {activeTab === "bodyFat" &&
                    `${t("Progress")}: ${
                      stats.bodyFatProgress < 0 ? "-" : ""
                    }${replaceDecimal(Math.abs(stats.bodyFatProgress).toFixed(1))}%`}

                  {activeTab === "waist" &&
                    `${t("Progress")}: ${
                      stats.waistProgress < 0 ? "-" : ""
                    }${replaceDecimal(Math.abs(stats.waistProgress).toFixed(1))} %`}
                </div>

                {/* Goal stat - shows based on active tab */}
                <div className="text-[#E50067] text-[12px] bg-[#FBDBEB] rounded-full px-2 py-1 flex items-center gap-2 cursor-default">
                  <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                    🎯
                  </p>
                  {activeTab === "weight" &&
                    `${t("Goal")}: ${formatValue(stats.goalWeight)}`}
                  {activeTab === "bodyFat" &&
                    `${t("Goal")}: ${formatValue(stats.goalBodyFat)}`}
                  {activeTab === "waist" &&
                    `${t("Goal")}: ${formatValue(stats.goalWaist)}`}
                </div>
              </div>
            )}
          </div>
          {/* Chart */}
          <div className="relative">
            {isLoading ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : hasData ? (
              <>
                {/* Scrollable container for all screens when data is large */}
                <div
                  className={`overflow-x-auto overflow-y-visible ${
                    needsScroll
                      ? "scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pb-2"
                      : ""
                  }`}
                  style={{
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: calculateChartWidth(),
                      minWidth: "100%",
                    }}
                  >
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart
                        data={currentChartData}
                        margin={{ top: 15, right: 30, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                          stroke="#D6D6D6"
                        />
                        <XAxis
                          dataKey="name"
                          axisLine={false}
                          tickLine={false}
                          tick={{
                            fill: "#6B7280",
                            fontSize: 12,
                          }}
                          padding={{ left: 15, right: 15 }}
                          interval={needsScroll ? "preserveStartEnd" : 0}
                          // padding={{ left: 10, right: 10 }}
                          tickFormatter={(value) => {
                            if (window.innerWidth < 768) {
                              const match = value.match(/Week\s*(\d+)/i);
                              return match ? `W${match[1]}` : value;
                            }
                            return value;
                          }}
                        />

                        <YAxis
                          width={80}
                          axisLine={false}
                          tickLine={false}
                          tick={{
                            fill: "#6B7280",
                            fontSize: 12,
                          }}
                          tickFormatter={(value) => formatValue(value)}
                          domain={getYAxisDomain()}
                        />
                        <Tooltip
                          formatter={(value) => [
                            formatValue(value),
                            getChartLabel(),
                          ]}
                          labelFormatter={(label) => `${label}`}
                        />
                        <Line
                          type="monotone"
                          dataKey={dataKey}
                          stroke="#3B82F6"
                          strokeWidth={3}
                          dot={{ r: 5, fill: "#3B82F6" }}
                          activeDot={{ r: 8, fill: "#3B82F6" }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Scroll hint for users */}
                {needsScroll && (
                  <div className="text-center mt-2">
                    <p className="text-xs text-gray-500">
                      ← {t("Scroll horizontally to see more data")} →
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <div className="text-center text-gray-500">
                  <svg
                    className="w-16 h-16 mx-auto mb-4 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <p className="text-[14px] font-[500] mb-1">
                    {t("No Data Available")}
                  </p>
                  <p className="text-[12px] text-gray-400">
                    {t("Your Physical Transformation")}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeekGraph;
